import {
  adminLeadDuplicateCandidateSetRevisionMaxLength,
  adminLeadDuplicateCursorMaxLength,
  adminLeadDuplicateEligibilities,
  adminLeadDuplicateMatchKinds,
  adminLeadDuplicatePageSizes,
  leadPipelineStages,
  type AdminLeadDuplicateCandidate,
  type AdminLeadDuplicateCandidateSetRevision,
  type AdminLeadDuplicateCursor,
  type AdminLeadDuplicateMatchKind,
  type AdminLeadDuplicatePage,
  type AdminLeadDuplicateSummary,
  type LeadId,
  type LeadPipelineStage,
} from "@whitehaven/contracts";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";

export const MAX_ADMIN_LEAD_DUPLICATE_COUNT = 2_147_483_647;

const eligibilitySet: ReadonlySet<string> = new Set(
  adminLeadDuplicateEligibilities,
);
const pipelineStageSet: ReadonlySet<string> = new Set(leadPipelineStages);

function hasExactOwnKeys(value: object, expected: readonly string[]): boolean {
  const keys = Object.keys(value);
  return (
    keys.length === expected.length &&
    expected.every((key) => Object.prototype.hasOwnProperty.call(value, key))
  );
}

function isCanonicalDisplayName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 200 &&
    value === value.trim() &&
    !/[\u0000\n\r]/.test(value)
  );
}

function isCanonicalTimestamp(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }

  const timestamp = new Date(value);
  return (
    Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value
  );
}

function isBoundedCount(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_ADMIN_LEAD_DUPLICATE_COUNT
  );
}

function isPositiveBoundedCount(value: unknown): value is number {
  return isBoundedCount(value) && value > 0;
}

export function isCanonicalAdminLeadDuplicateMatchKinds(
  value: unknown,
  allowEmpty = false,
): value is readonly AdminLeadDuplicateMatchKind[] {
  if (!Array.isArray(value)) return false;
  if (allowEmpty && value.length === 0) return true;

  return (
    (value.length === 1 &&
      (value[0] === adminLeadDuplicateMatchKinds[0] ||
        value[0] === adminLeadDuplicateMatchKinds[1])) ||
    (value.length === 2 &&
      value[0] === adminLeadDuplicateMatchKinds[0] &&
      value[1] === adminLeadDuplicateMatchKinds[1])
  );
}

export function isAdminLeadDuplicateSummaryConsistent(
  value: unknown,
): value is AdminLeadDuplicateSummary {
  if (
    typeof value !== "object" ||
    value === null ||
    !hasExactOwnKeys(value, [
      "eligibility",
      "candidateContactCount",
      "matchKinds",
    ])
  ) {
    return false;
  }

  const summary = value as AdminLeadDuplicateSummary;
  if (
    typeof summary.eligibility !== "string" ||
    !eligibilitySet.has(summary.eligibility) ||
    !isBoundedCount(summary.candidateContactCount) ||
    !isCanonicalAdminLeadDuplicateMatchKinds(summary.matchKinds, true)
  ) {
    return false;
  }

  return summary.eligibility === "TARGET_INACTIVE"
    ? summary.candidateContactCount === 0 && summary.matchKinds.length === 0
    : summary.candidateContactCount === 0
      ? summary.matchKinds.length === 0
      : summary.matchKinds.length > 0;
}

export function isAdminLeadDuplicateCandidateConsistent(
  value: unknown,
): value is AdminLeadDuplicateCandidate {
  if (
    typeof value !== "object" ||
    value === null ||
    !hasExactOwnKeys(value, [
      "representativeLeadId",
      "displayName",
      "matchKinds",
      "activeLeadCount",
      "pipelineStage",
      "representativeLeadCreatedAt",
    ])
  ) {
    return false;
  }

  const candidate = value as AdminLeadDuplicateCandidate;
  return (
    typeof candidate.representativeLeadId === "string" &&
    isSafeLeadIdPathSegment(candidate.representativeLeadId) &&
    isCanonicalDisplayName(candidate.displayName) &&
    isCanonicalAdminLeadDuplicateMatchKinds(candidate.matchKinds) &&
    isPositiveBoundedCount(candidate.activeLeadCount) &&
    typeof candidate.pipelineStage === "string" &&
    pipelineStageSet.has(candidate.pipelineStage) &&
    isCanonicalTimestamp(candidate.representativeLeadCreatedAt)
  );
}

export function isAdminLeadDuplicateCandidateSetRevision(
  value: unknown,
): value is AdminLeadDuplicateCandidateSetRevision {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= adminLeadDuplicateCandidateSetRevisionMaxLength &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

export function isAdminLeadDuplicateCursor(
  value: unknown,
): value is AdminLeadDuplicateCursor {
  return (
    typeof value === "string" &&
    value.length >= 8 &&
    value.length <= adminLeadDuplicateCursorMaxLength &&
    /^[A-Za-z0-9_-]+$/.test(value)
  );
}

function summaryContainsCandidateKinds(
  summary: AdminLeadDuplicateSummary,
  candidate: AdminLeadDuplicateCandidate,
): boolean {
  return candidate.matchKinds.every((kind) =>
    summary.matchKinds.includes(kind),
  );
}

export function adminLeadDuplicateSummaryMatchesCompleteCandidates(
  summary: AdminLeadDuplicateSummary,
  candidates: readonly AdminLeadDuplicateCandidate[],
): boolean {
  if (summary.candidateContactCount !== candidates.length) return false;

  const union = adminLeadDuplicateMatchKinds.filter((kind) =>
    candidates.some((candidate) => candidate.matchKinds.includes(kind)),
  );
  return (
    summary.matchKinds.length === union.length &&
    summary.matchKinds.every((kind, index) => union[index] === kind)
  );
}

export function adminLeadDuplicateSummariesEqual(
  left: AdminLeadDuplicateSummary,
  right: AdminLeadDuplicateSummary,
): boolean {
  return (
    left.eligibility === right.eligibility &&
    left.candidateContactCount === right.candidateContactCount &&
    left.matchKinds.length === right.matchKinds.length &&
    left.matchKinds.every((kind, index) => right.matchKinds[index] === kind)
  );
}

export function isAdminLeadDuplicatePageConsistent(
  value: unknown,
  expectedLeadId: LeadId,
  requestedCursor: AdminLeadDuplicateCursor | null = null,
  expectedCandidateSetRevision: AdminLeadDuplicateCandidateSetRevision | null = null,
): value is AdminLeadDuplicatePage {
  if (
    typeof value !== "object" ||
    value === null ||
    !hasExactOwnKeys(value, [
      "leadId",
      "candidateSetRevision",
      "summary",
      "items",
      "nextCursor",
    ])
  ) {
    return false;
  }

  const page = value as AdminLeadDuplicatePage;
  if (
    page.leadId !== expectedLeadId ||
    !isAdminLeadDuplicateCandidateSetRevision(page.candidateSetRevision) ||
    (expectedCandidateSetRevision !== null &&
      page.candidateSetRevision !== expectedCandidateSetRevision) ||
    !isAdminLeadDuplicateSummaryConsistent(page.summary) ||
    !Array.isArray(page.items) ||
    page.items.length > adminLeadDuplicatePageSizes[0] ||
    (page.nextCursor !== null &&
      !isAdminLeadDuplicateCursor(page.nextCursor)) ||
    (requestedCursor !== null && page.nextCursor === requestedCursor) ||
    (page.items.length === 0 && page.nextCursor !== null) ||
    (page.nextCursor !== null &&
      page.items.length !== adminLeadDuplicatePageSizes[0]) ||
    page.summary.candidateContactCount < page.items.length
  ) {
    return false;
  }

  if (
    page.summary.eligibility === "TARGET_INACTIVE" &&
    (page.items.length !== 0 || page.nextCursor !== null)
  ) {
    return false;
  }

  if (
    requestedCursor === null &&
    ((page.nextCursor === null &&
      page.summary.candidateContactCount !== page.items.length) ||
      (page.nextCursor !== null &&
        page.summary.candidateContactCount <= page.items.length))
  ) {
    return false;
  }

  const representativeLeadIds = new Set<string>();
  for (const candidate of page.items) {
    if (
      !isAdminLeadDuplicateCandidateConsistent(candidate) ||
      candidate.representativeLeadId === expectedLeadId ||
      representativeLeadIds.has(candidate.representativeLeadId) ||
      !summaryContainsCandidateKinds(page.summary, candidate)
    ) {
      return false;
    }
    representativeLeadIds.add(candidate.representativeLeadId);
  }

  return requestedCursor !== null || page.nextCursor !== null
    ? true
    : adminLeadDuplicateSummaryMatchesCompleteCandidates(
        page.summary,
        page.items,
      );
}

export function mergeAdminLeadDuplicateCandidates(
  current: readonly AdminLeadDuplicateCandidate[],
  incoming: readonly AdminLeadDuplicateCandidate[],
): readonly AdminLeadDuplicateCandidate[] | null {
  const representativeLeadIds = new Set(
    current.map((candidate) => candidate.representativeLeadId),
  );
  for (const candidate of incoming) {
    if (representativeLeadIds.has(candidate.representativeLeadId)) return null;
    representativeLeadIds.add(candidate.representativeLeadId);
  }
  return [...current, ...incoming];
}

export function duplicateMatchKindsLabel(
  matchKinds: readonly AdminLeadDuplicateMatchKind[],
): string {
  return matchKinds.length === 2
    ? "Email and phone"
    : matchKinds[0] === "EMAIL"
      ? "Email"
      : "Phone";
}

export function isLeadPipelineStage(value: string): value is LeadPipelineStage {
  return pipelineStageSet.has(value);
}
